import React, { useEffect, useMemo, useState } from "react";
import { ExternalLink, LogOut, ShieldAlert } from "lucide-react";
import { logoutFromThisApp } from "../../auth/logout";
import {
  fetchAllApplications,
  getApplicationDisplayName,
  openAssignedApplication,
  parseAssignedApplications,
  readLoginSelectedApplication,
  resolveAssignedApplicationsForSession,
  resolveSelectedApplicationDisplayName,
  type AssignedApplication,
} from "../../utils/matchesAssignedApplicationOrigin";
import { readCachedSessionUser } from "../../auth/session/authSession.storage";

const ApplicationNotAssigned: React.FC = () => {
  const authUser = readCachedSessionUser();
  const selection = readLoginSelectedApplication();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const assignedApplications = useMemo(() => {
    const fromSession = resolveAssignedApplicationsForSession(authUser);
    if (fromSession.length > 0) return fromSession;
    return parseAssignedApplications(authUser?.applications);
  }, [authUser]);

  const [selectedLabel, setSelectedLabel] = useState(() =>
    resolveSelectedApplicationDisplayName(selection, assignedApplications),
  );

  useEffect(() => {
    const syncLabel = resolveSelectedApplicationDisplayName(
      selection,
      assignedApplications,
    );
    if (syncLabel !== "the application you selected") {
      setSelectedLabel(syncLabel);
      return;
    }

    let cancelled = false;
    void fetchAllApplications().then((registry) => {
      if (cancelled) return;
      setSelectedLabel(
        resolveSelectedApplicationDisplayName(selection, [
          ...assignedApplications,
          ...registry,
        ]),
      );
    });

    return () => {
      cancelled = true;
    };
  }, [selection, assignedApplications]);

  const handleOpenAssignedApp = (app: AssignedApplication) => {
    void openAssignedApplication(app);
  };

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await logoutFromThisApp();
    } catch {
      setIsLoggingOut(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#0c1018] px-6 py-12">
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 h-[420px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-amber-500/10 blur-3xl"
      />

      <div className="relative w-full max-w-lg rounded-2xl border border-slate-700/80 bg-[#161b22] p-8 shadow-xl shadow-black/40">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-500/10">
            <ShieldAlert className="h-5 w-5 text-amber-400" aria-hidden />
          </div>
          <h1 className="text-xl font-semibold text-white">Application not assigned</h1>
        </div>

        <p className="text-sm leading-relaxed text-slate-400">
          Your account is not assigned to{" "}
          <strong className="font-semibold text-white">{selectedLabel}</strong>. Please contact
          your administrator to request access, or open one of your assigned applications below.
        </p>

        {assignedApplications.length > 0 ? (
          <ul className="mt-8 space-y-3">
            {assignedApplications.map((app) => (
              <li key={app.id || app.url}>
                <button
                  type="button"
                  onClick={() => handleOpenAssignedApp(app)}
                  className="flex w-full items-center justify-between rounded-xl border border-slate-700 bg-[#0d1117] px-4 py-3 text-left text-sm text-white transition hover:border-slate-500 hover:bg-slate-900/60"
                >
                  <span className="font-medium">{getApplicationDisplayName(app)}</span>
                  <ExternalLink className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-6 text-sm text-slate-400">
            No applications are currently assigned to your account.
          </p>
        )}

        <div className="mt-8 border-t border-slate-700/80 pt-6">
          <button
            type="button"
            onClick={() => void handleLogout()}
            disabled={isLoggingOut}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-rose-400/70 bg-transparent px-4 py-3 text-sm font-medium text-rose-400 transition hover:border-rose-400 hover:bg-rose-500/5 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <LogOut className="h-4 w-4 shrink-0" aria-hidden />
            {isLoggingOut ? "Logging out…" : "Log out"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApplicationNotAssigned;
